import React from "react";
import { Container, Grid, Typography, Box, Card, Divider } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SectionHeading from "./SectionHeading";
import useReveal from "./useReveal";
import { brand, gradients } from "../theme";

const caseStudies = [
  {
    tag: "Institutional Internship Program",
    metric: "82%",
    metricLabel: "first-round pass rate",
    challenge:
      "A regional engineering college saw fewer than 35% of students clearing preliminary coding screening rounds, due to legacy lab routines.",
    solution:
      "We deployed a 45-day milestone-driven full-stack internship, transitioning students onto our cloud sandbox with active microservices development tracks.",
    outcome:
      "First-round interview pass rates climbed to 82%. Over 410 GitHub-verified portfolios led to tier-1 consulting placements within 9 months.",
  },
  {
    tag: "Technology Skill Development Initiative",
    metric: "40%",
    metricLabel: "faster analytics execution",
    challenge:
      "An expanding polytechnic cluster lacked direct industrial validation tracks, causing graduates to face immediate onboarding hurdles.",
    solution:
      "Our software wing deployed high-intensity capability bootcamps linked with automated sandbox evaluations and structured codebase audits.",
    outcome:
      "Core analytics execution times improved by 40%, with deans reporting a dramatic spike in recruitment compliance and technical submissions.",
  },
];

const comparison = [
  {
    metric: "Hands-on Learning",
    traditional: "Syntax memorization & passive slides",
    zealous: "High-intensity sprints & automated code audits",
  },
  {
    metric: "Industry Projects",
    traditional: "Local compilation, no version tracking",
    zealous: "Mandatory Git/GitHub tracking & logic evaluations",
  },
  {
    metric: "Mentoring & Code Support",
    traditional: "Trainers lacking commercial experience",
    zealous: "Active industry practitioners & cloud consultants",
  },
  {
    metric: "Product Exposure",
    traditional: "None beyond the user interface",
    zealous: "Direct interaction with live platforms like Profoundry",
  },
  {
    metric: "Career Preparation",
    traditional: "Narrow syntax instruction only",
    zealous: "ATS resumes, algorithmic bootcamps & mock interviews",
  },
];

const CaseStudies = () => {
  const ref = useReveal();
  return (
    <section id="casestudies-section" className="casestudies-section" ref={ref}>
      <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: "#fff" }}>
        <Container>
          <SectionHeading
            eyebrow="SUCCESS STORIES"
            title="Empirical Case Studies"
            subtitle="Verifiable proof of how we transform institutional capabilities and solve complex business-logic challenges."
          />

          <Grid container spacing={4}>
            {caseStudies.map((c) => (
              <Grid item xs={12} md={6} key={c.tag} className="reveal">
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: { xs: 3, md: 4 },
                    border: "1px solid rgba(12,131,200,0.12)",
                    transition: "transform .3s ease, box-shadow .3s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: "0 22px 48px rgba(15,39,64,0.12)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        borderRadius: "14px",
                        background: gradients.brand,
                        color: "#fff",
                        textAlign: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, fontSize: "1.6rem", lineHeight: 1 }}>
                        {c.metric}
                      </Typography>
                      <Typography sx={{ fontSize: "0.68rem", opacity: 0.9 }}>{c.metricLabel}</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: brand.navy, fontWeight: 700, fontSize: "1.05rem" }}>
                      {c.tag}
                    </Typography>
                  </Box>

                  {[
                    { label: "The Challenge", text: c.challenge, color: brand.orange },
                    { label: "The Solution", text: c.solution, color: brand.blue },
                    { label: "The Outcome", text: c.outcome, color: brand.success || "#16a34a" },
                  ].map((row) => (
                    <Box key={row.label} sx={{ mb: 2 }}>
                      <Typography
                        sx={{ color: row.color, fontWeight: 700, fontSize: "0.8rem", letterSpacing: "0.05em", mb: 0.5 }}
                      >
                        {row.label.toUpperCase()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: brand.slate }}>
                        {row.text}
                      </Typography>
                    </Box>
                  ))}
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Comparison */}
          <Box sx={{ mt: { xs: 7, md: 10 } }} className="reveal">
            <Typography
              variant="h4"
              align="center"
              sx={{ color: brand.navy, fontWeight: 800, mb: 1, fontSize: { xs: "1.5rem", md: "2rem" } }}
            >
              Why Institutions Partner With Us
            </Typography>
            <Typography align="center" sx={{ color: brand.slate, mb: 4, maxWidth: 640, mx: "auto" }}>
              An objective comparison between conventional training vendors and the Zealous
              Tech Corp architecture.
            </Typography>

            <Card elevation={0} sx={{ border: "1px solid rgba(12,131,200,0.12)", overflow: "hidden" }}>
              {/* Header row */}
              <Grid container sx={{ bgcolor: brand.navy, color: "#fff" }}>
                <Grid item xs={4} sx={{ p: { xs: 1.5, md: 2.5 } }}>
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: "0.8rem", md: "0.95rem" } }}>
                    Strategic Focus
                  </Typography>
                </Grid>
                <Grid item xs={4} sx={{ p: { xs: 1.5, md: 2.5 }, borderLeft: "1px solid rgba(255,255,255,0.12)" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: "0.8rem", md: "0.95rem" }, opacity: 0.85 }}>
                    Traditional Vendor
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{ p: { xs: 1.5, md: 2.5 }, background: brand.blue, display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <TrendingUpIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: "0.8rem", md: "0.95rem" } }}>
                    Zealous Tech Corp
                  </Typography>
                </Grid>
              </Grid>

              {comparison.map((row, i) => (
                <React.Fragment key={row.metric}>
                  <Grid container sx={{ bgcolor: i % 2 === 0 ? "#fff" : brand.bg }}>
                    <Grid item xs={4} sx={{ p: { xs: 1.5, md: 2.5 } }}>
                      <Typography sx={{ fontWeight: 700, color: brand.navy, fontSize: { xs: "0.78rem", md: "0.9rem" } }}>
                        {row.metric}
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ p: { xs: 1.5, md: 2.5 }, borderLeft: "1px solid rgba(0,0,0,0.06)" }}>
                      <Box sx={{ display: "flex", gap: 0.75 }}>
                        <CloseIcon sx={{ fontSize: 16, color: "#cbd5e1", mt: "2px", flexShrink: 0 }} />
                        <Typography sx={{ color: brand.slate, fontSize: { xs: "0.74rem", md: "0.85rem" } }}>
                          {row.traditional}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: "rgba(12,131,200,0.05)", borderLeft: "1px solid rgba(12,131,200,0.10)" }}
                    >
                      <Box sx={{ display: "flex", gap: 0.75 }}>
                        <CheckIcon sx={{ fontSize: 16, color: brand.blue, mt: "2px", flexShrink: 0 }} />
                        <Typography sx={{ color: brand.navy, fontWeight: 500, fontSize: { xs: "0.74rem", md: "0.85rem" } }}>
                          {row.zealous}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {i < comparison.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Card>
          </Box>
        </Container>
      </Box>
    </section>
  );
};

export default CaseStudies;
